import { useEffect, useState } from 'react';
import { loadExternalScript } from '../utils/scriptLoader';
import {
  DATA_YEARS,
  DISCORD_CSV_BY_YEAR,
  SOCIAL_CSV_BY_YEAR,
  getCsvUrls,
} from '../utils/dataSources';

const D3_URL = 'https://d3js.org/d3.v7.min.js';
const PLOT_URL = 'https://cdn.jsdelivr.net/npm/@observablehq/plot@0.6/dist/plot.umd.min.js';
const DISCORD_CSVS = getCsvUrls(DISCORD_CSV_BY_YEAR);
const SOCIAL_CSVS = DATA_YEARS
  .map((year) => ({ year, url: SOCIAL_CSV_BY_YEAR[year] }))
  .filter(({ url }) => Boolean(url));

const TWO_DIGIT_YEAR_BASE = 2000;

const parseDate = (value) => {
  if (!value) return null;
  const [month, day, year] = value.split('/').map((segment) => segment.trim());
  if (!month || !day || !year) return null;
  const monthIndex = Number.parseInt(month, 10) - 1;
  const dayNumber = Number.parseInt(day, 10);
  let yearNumber = Number.parseInt(year, 10);
  if (Number.isNaN(monthIndex) || Number.isNaN(dayNumber) || Number.isNaN(yearNumber)) return null;
  if (year.length === 2) {
    yearNumber += yearNumber >= 90 ? 1900 : TWO_DIGIT_YEAR_BASE;
  }
  return new Date(yearNumber, monthIndex, dayNumber);
};

const parseCurrency = (value) => {
  if (value == null || value === '') return 0;
  const strValue = String(value).trim();
  if (!strValue) return 0;
  if (strValue.startsWith('(') && strValue.endsWith(')')) {
    const parsed = Number.parseFloat(strValue.slice(1, -1).replace(/[$,]/g, ''));
    return Number.isNaN(parsed) ? 0 : -parsed;
  }
  const cleaned = strValue.replace(/[^0-9.-]+/g, '');
  const parsed = Number.parseFloat(cleaned);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const parseInteger = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export function useBonusesData() {
  const [state, setState] = useState({
    loading: true,
    error: null,
    d3: null,
    Plot: null,
    rows: [],
    blackjackTotalsByYear: {},
  });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const d3Lib = await loadExternalScript(D3_URL, 'd3');
        const plotLib = await loadExternalScript(PLOT_URL, 'Plot');

        if (cancelled) return;

        const rawRowSets = await Promise.all(DISCORD_CSVS.map((url) => d3Lib.csv(url)));
        if (cancelled) return;
        const rawRows = rawRowSets.flat();

        const socialResults = await Promise.allSettled(
          SOCIAL_CSVS.map(({ url }) => d3Lib.csv(url))
        );
        if (cancelled) return;

        const blackjackTotalsByYear = socialResults.reduce((acc, result, index) => {
          if (result.status !== 'fulfilled') return acc;
          const row = result.value?.[0];
          if (!row) return acc;
          const { year } = SOCIAL_CSVS[index];
          acc[year] = {
            totalHands: parseInteger(row.black_jack_hands_played_total || row.bj_total_hands),
            dealerHas10: parseInteger(row.black_jack_dealer_has_10_count || row.bj_dealer_has_10),
          };
          return acc;
        }, {});

        const rows = rawRows
          .map((row) => {
            const date = parseDate(row.date);
            if (!date) return null;
            return {
              date,
              qotdSuccess: row.qotd_success === 'true',
              numBonuses: Number.parseFloat(row.num_bonuses) || 0,
              largestBonusAmount: parseCurrency(row.largest_bonus_amount),
              largestBonusSlot: row.largest_bonus_slot || 'Unknown',
              slots: row.slots || '',
              handpays: Number.parseFloat(row.num_handpays) || 0,
              getEven: parseCurrency(row.get_even),
              tableGames: row.table_games || '',
              tableGamesTotal: parseCurrency(row.table_games_total),
              hwmYtd: parseCurrency(row.hwm_ytd),
              bjTotalHands: parseInteger(row.black_jack_hands_played_total || row.bj_total_hands),
              bjDealerHas10: parseInteger(row.black_jack_dealer_has_10_count || row.bj_dealer_has_10),
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.date - b.date);

        if (cancelled) return;

        setState({
          loading: false,
          error: null,
          d3: d3Lib,
          Plot: plotLib,
          rows,
          blackjackTotalsByYear,
        });
      } catch (error) {
        if (cancelled) return;
        setState({
          loading: false,
          error,
          d3: null,
          Plot: null,
          rows: [],
          blackjackTotalsByYear: {},
        });
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

export function formatCurrency(value, { includeCents = false } = {}) {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }

  const sign = value < 0 ? '-' : '';
  const absolute = Math.abs(value);
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: includeCents ? 2 : 0,
    maximumFractionDigits: includeCents ? 2 : 0,
  });
  return `${sign}$${formatter.format(absolute)}`;
}

export function formatPercentage(value, fractionDigits = 1) {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  return `${value.toFixed(fractionDigits)}%`;
}

export function formatInteger(value) {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US').format(Math.round(value));
}
